import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import http from '../../services/http';
import { useCitizen } from '../../CitizenContext';

const EMPTY = {
  completionStatus: 'not_started',
  consentStatus: 'not_asked',
  householdReferenceId: '',
  householdSize: '',
  usualResidents: '',
  childrenUnder5: '',
  olderAdults60Plus: '',
  educationHighestLevel: '',
  schoolAttendanceStatus: '',
  nutritionConcern: '',
  maternalHealthAccessConcern: '',
  childAdolescentMortalityHistory: '',
  housingType: '',
  housingTenure: '',
  roomsUsedForSleeping: '',
  drinkingWaterSource: '',
  sanitationFacility: '',
  cookingFuel: '',
  electricityAccess: '',
  incomeDisclosurePreference: 'not_asked',
  householdIncomeBand: '',
  financialStrain: '',
  bankAccountAccess: '',
  healthCoverage: '',
  assetsText: '',
  socialProtectionSchemesText: '',
  notes: '',
  nextReviewDueAt: '',
  reminderDeferredUntil: '',
};

const OPTIONS = {
  yesNoUnknown: ['Yes', 'No', 'Unknown', 'Not assessed', 'Prefer not to say'],
  education: [
    'Not assessed',
    'No formal schooling',
    'Primary',
    'Middle',
    'Secondary',
    'Senior secondary',
    'Graduate',
    'Postgraduate or higher',
    'Other / mixed household',
    'Prefer not to say',
  ],
  attendance: [
    'Not assessed',
    'No school-age child in household',
    'All school-age children attending',
    'One or more not attending regularly',
    'Unknown',
    'Prefer not to say',
  ],
  housing: [
    'Not assessed',
    'Kutcha',
    'Semi-pucca',
    'Pucca',
    'Temporary / makeshift',
    'Other',
  ],
  tenure: ['Not assessed', 'Owned', 'Rented', 'Employer provided', 'Shared / family', 'Other'],
  water: [
    'Not assessed',
    'Piped supply into dwelling/yard',
    'Public tap / standpost',
    'Tube well / bore well',
    'Protected well / spring',
    'Tanker / vendor',
    'Surface water',
    'Other',
  ],
  sanitation: [
    'Not assessed',
    'Flush / pour-flush toilet',
    'Pit latrine',
    'Shared sanitation facility',
    'No toilet / open defecation',
    'Other',
  ],
  fuel: [
    'Not assessed',
    'LPG / PNG / electricity',
    'Kerosene',
    'Wood / crop residue / dung / biomass',
    'Coal / charcoal',
    'Mixed fuels',
    'Other',
  ],
  electricity: ['Not assessed', 'Available', 'Not available', 'Intermittent / unreliable'],
  financialStrain: [
    'Not assessed',
    'No difficulty reported',
    'Occasional difficulty meeting essential expenses',
    'Frequent difficulty meeting essential expenses',
    'Severe current financial difficulty',
    'Prefer not to say',
  ],
  bank: ['Not assessed', 'At least one household member has access', 'No access reported', 'Unknown', 'Prefer not to say'],
  coverage: [
    'Not assessed',
    'Government health coverage / entitlement',
    'Employer / ESI or similar coverage',
    'Private insurance',
    'Multiple sources',
    'No coverage known',
    'Unknown',
  ],
};

function dateInputValue(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

function asForm(context = {}) {
  return {
    ...EMPTY,
    ...context,
    householdSize: context.householdSize ?? '',
    usualResidents: context.usualResidents ?? '',
    childrenUnder5: context.childrenUnder5 ?? '',
    olderAdults60Plus: context.olderAdults60Plus ?? '',
    roomsUsedForSleeping: context.roomsUsedForSleeping ?? '',
    assetsText: Array.isArray(context.assets) ? context.assets.join(', ') : '',
    socialProtectionSchemesText: Array.isArray(context.socialProtectionSchemes)
      ? context.socialProtectionSchemes.join(', ')
      : '',
    nextReviewDueAt: dateInputValue(context.nextReviewDueAt),
    reminderDeferredUntil: dateInputValue(context.reminderDeferredUntil),
  };
}

function splitList(value) {
  return String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function numericOrUndefined(value) {
  if (value === '' || value === null || value === undefined) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function Section({ title, children }) {
  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2 }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>{title}</Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
          gap: 2,
        }}
      >
        {children}
      </Box>
    </Paper>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <TextField select fullWidth label={label} value={value || ''} onChange={onChange}>
      <MenuItem value=""><em>Select</em></MenuItem>
      {options.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
    </TextField>
  );
}

export default function SocialHouseholdContext() {
  const { citizenId = '' } = useParams();
  const { citizen: activeCitizen, setCitizen } = useCitizen();
  const [citizenName, setCitizenName] = useState('');
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const statusLabel = useMemo(() => {
    const status = String(form.completionStatus || 'not_started');
    if (status === 'complete') return 'Complete';
    if (status === 'in_progress') return 'In progress';
    if (status === 'deferred') return 'Deferred';
    return 'Not started';
  }, [form.completionStatus]);

  const setField = (field) => (event) => {
    const value = event?.target?.value ?? '';
    setForm((current) => ({
      ...current,
      [field]: value,
      completionStatus:
        current.completionStatus === 'not_started' ? 'in_progress' : current.completionStatus,
    }));
    setNotice('');
  };

  useEffect(() => {
    if (!citizenId) {
      setError('Citizen ID is required.');
      setLoading(false);
      return;
    }

    let alive = true;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const { data } = await http.get(
          `/api/citizens/${encodeURIComponent(citizenId)}/social-household-context`
        );
        if (!alive) return;
        setCitizenName(data?.name || '');
        setForm(asForm(data?.socialHouseholdContext || {}));
      } catch (err) {
        if (!alive) return;
        setError(err?.response?.data?.message || err?.message || 'Unable to load Social & Household Context.');
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => { alive = false; };
  }, [citizenId]);

  const buildPayload = (overrides = {}) => ({
    completionStatus: form.completionStatus || 'in_progress',
    consentStatus: form.consentStatus || 'not_asked',
    householdReferenceId: form.householdReferenceId,
    householdSize: numericOrUndefined(form.householdSize),
    usualResidents: numericOrUndefined(form.usualResidents),
    childrenUnder5: numericOrUndefined(form.childrenUnder5),
    olderAdults60Plus: numericOrUndefined(form.olderAdults60Plus),
    educationHighestLevel: form.educationHighestLevel,
    schoolAttendanceStatus: form.schoolAttendanceStatus,
    nutritionConcern: form.nutritionConcern,
    maternalHealthAccessConcern: form.maternalHealthAccessConcern,
    childAdolescentMortalityHistory: form.childAdolescentMortalityHistory,
    housingType: form.housingType,
    housingTenure: form.housingTenure,
    roomsUsedForSleeping: numericOrUndefined(form.roomsUsedForSleeping),
    drinkingWaterSource: form.drinkingWaterSource,
    sanitationFacility: form.sanitationFacility,
    cookingFuel: form.cookingFuel,
    electricityAccess: form.electricityAccess,
    incomeDisclosurePreference: form.incomeDisclosurePreference || 'not_asked',
    householdIncomeBand:
      form.incomeDisclosurePreference === 'income_band_provided' ? form.householdIncomeBand : '',
    financialStrain: form.financialStrain,
    bankAccountAccess: form.bankAccountAccess,
    healthCoverage: form.healthCoverage,
    assets: splitList(form.assetsText),
    socialProtectionSchemes: splitList(form.socialProtectionSchemesText),
    notes: form.notes,
    nextReviewDueAt: form.nextReviewDueAt || undefined,
    reminderDeferredUntil: form.reminderDeferredUntil || undefined,
    ...overrides,
  });

  const save = async (overrides = {}) => {
    try {
      setSaving(true);
      setError('');
      setNotice('');
      const payload = buildPayload(overrides);
      const { data } = await http.patch(
        `/api/citizens/${encodeURIComponent(citizenId)}/social-household-context`,
        payload
      );
      const next = data?.socialHouseholdContext || payload;
      setForm(asForm(next));
      try {
        const cached = JSON.parse(localStorage.getItem('activeCitizen') || 'null');
        if (cached?.citizenId === citizenId) {
          localStorage.setItem('activeCitizen', JSON.stringify({
            ...cached,
            socialHouseholdContext: next,
          }));
        }
      } catch {}
      if (activeCitizen?.citizenId === citizenId) {
        setCitizen({ ...activeCitizen, socialHouseholdContext: next });
      }
      setNotice(data?.message || 'Social & Household Context saved.');
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Unable to save Social & Household Context.');
    } finally {
      setSaving(false);
    }
  };

  const deferReminder = async () => {
    const until = new Date();
    until.setMonth(until.getMonth() + 3);
    await save({
      completionStatus: 'deferred',
      reminderDeferredUntil: until.toISOString(),
    });
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', gap: 1.5, alignItems: 'center' }}>
        <CircularProgress size={24} />
        <Typography>Loading Social & Household Context…</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>Social & Household Context</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {citizenName || 'Citizen'} · {citizenId}
            </Typography>
          </Box>
          <Chip label={statusLabel} color={form.completionStatus === 'complete' ? 'success' : 'default'} />
        </Box>
      </Paper>

      <Alert severity="info">
        Ask these questions respectfully and explain that they help understand circumstances that may affect health and access to care. Exact income is not required; a broad band, “not sure”, or “prefer not to say” is acceptable. OPD care must not be delayed because this profile is incomplete.
      </Alert>

      {error ? <Alert severity="error">{error}</Alert> : null}
      {notice ? <Alert severity="success">{notice}</Alert> : null}

      <Section title="Collection status">
        <SelectField
          label="Permission / willingness to discuss"
          value={form.consentStatus}
          onChange={setField('consentStatus')}
          options={['not_asked', 'agreed', 'declined']}
        />
        <TextField
          fullWidth
          label="Household reference (optional)"
          value={form.householdReferenceId}
          onChange={setField('householdReferenceId')}
          helperText="Reserved for linking household members without duplicating shared information."
        />
      </Section>

      <Section title="Household composition">
        <TextField type="number" inputProps={{ min: 1 }} label="Household size" value={form.householdSize} onChange={setField('householdSize')} />
        <TextField type="number" inputProps={{ min: 1 }} label="Usual residents" value={form.usualResidents} onChange={setField('usualResidents')} />
        <TextField type="number" inputProps={{ min: 0 }} label="Children under 5 years" value={form.childrenUnder5} onChange={setField('childrenUnder5')} />
        <TextField type="number" inputProps={{ min: 0 }} label="Adults aged 60 years or more" value={form.olderAdults60Plus} onChange={setField('olderAdults60Plus')} />
      </Section>

      <Section title="Education and selected health-related household indicators">
        <SelectField label="Highest education level in household" value={form.educationHighestLevel} onChange={setField('educationHighestLevel')} options={OPTIONS.education} />
        <SelectField label="School attendance" value={form.schoolAttendanceStatus} onChange={setField('schoolAttendanceStatus')} options={OPTIONS.attendance} />
        <SelectField label="Nutrition concern" value={form.nutritionConcern} onChange={setField('nutritionConcern')} options={OPTIONS.yesNoUnknown} />
        <SelectField label="Maternal-health access concern" value={form.maternalHealthAccessConcern} onChange={setField('maternalHealthAccessConcern')} options={OPTIONS.yesNoUnknown} />
        <SelectField label="Child/adolescent mortality history" value={form.childAdolescentMortalityHistory} onChange={setField('childAdolescentMortalityHistory')} options={OPTIONS.yesNoUnknown} />
      </Section>

      <Section title="Housing and basic services">
        <SelectField label="Housing type" value={form.housingType} onChange={setField('housingType')} options={OPTIONS.housing} />
        <SelectField label="Housing tenure" value={form.housingTenure} onChange={setField('housingTenure')} options={OPTIONS.tenure} />
        <TextField type="number" inputProps={{ min: 0 }} label="Rooms used for sleeping" value={form.roomsUsedForSleeping} onChange={setField('roomsUsedForSleeping')} />
        <SelectField label="Drinking-water source" value={form.drinkingWaterSource} onChange={setField('drinkingWaterSource')} options={OPTIONS.water} />
        <SelectField label="Sanitation facility" value={form.sanitationFacility} onChange={setField('sanitationFacility')} options={OPTIONS.sanitation} />
        <SelectField label="Main cooking fuel" value={form.cookingFuel} onChange={setField('cookingFuel')} options={OPTIONS.fuel} />
        <SelectField label="Electricity access" value={form.electricityAccess} onChange={setField('electricityAccess')} options={OPTIONS.electricity} />
      </Section>

      <Section title="Financial access and social protection">
        <SelectField
          label="Income information"
          value={form.incomeDisclosurePreference}
          onChange={setField('incomeDisclosurePreference')}
          options={['not_asked', 'prefer_not_to_say', 'income_band_provided']}
        />
        <TextField
          select
          fullWidth
          label="Approximate household income band"
          value={form.householdIncomeBand}
          onChange={setField('householdIncomeBand')}
          disabled={form.incomeDisclosurePreference !== 'income_band_provided'}
          helperText="Use a broad band only; exact income is not required."
        >
          <MenuItem value=""><em>Select</em></MenuItem>
          {[
            'Variable / irregular / not sure',
            'Below ₹10,000 per month',
            '₹10,000–24,999 per month',
            '₹25,000–49,999 per month',
            '₹50,000–99,999 per month',
            '₹1,00,000 or more per month',
          ].map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
        </TextField>
        <SelectField label="Difficulty meeting essential expenses" value={form.financialStrain} onChange={setField('financialStrain')} options={OPTIONS.financialStrain} />
        <SelectField label="Bank-account access" value={form.bankAccountAccess} onChange={setField('bankAccountAccess')} options={OPTIONS.bank} />
        <SelectField label="Health coverage / entitlement" value={form.healthCoverage} onChange={setField('healthCoverage')} options={OPTIONS.coverage} />
        <TextField fullWidth label="Relevant household assets (comma separated)" value={form.assetsText} onChange={setField('assetsText')} />
        <TextField fullWidth label="Social-protection schemes / benefits (comma separated)" value={form.socialProtectionSchemesText} onChange={setField('socialProtectionSchemesText')} />
      </Section>

      <Section title="Review and notes">
        <TextField
          fullWidth
          type="date"
          InputLabelProps={{ shrink: true }}
          label="Next review due"
          value={form.nextReviewDueAt}
          onChange={setField('nextReviewDueAt')}
        />
        <TextField
          fullWidth
          multiline
          minRows={3}
          label="Context / notes"
          value={form.notes}
          onChange={setField('notes')}
          sx={{ gridColumn: { md: '1 / -1' } }}
        />
      </Section>

      <Alert severity="warning">
        This Phase 4A.1 profile stores structured, MPI-aligned source information but does not calculate or display an official Indian MPI/MDPI score. A verified methodology/version will be added before any formal score is produced.
      </Alert>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 1.5, pb: 2 }}>
        <Button variant="outlined" disabled={saving} onClick={deferReminder}>Remind later</Button>
        <Button variant="outlined" disabled={saving} onClick={() => save({ completionStatus: 'in_progress' })}>
          {saving ? 'Saving…' : 'Save progress'}
        </Button>
        <Button variant="contained" disabled={saving} onClick={() => save({ completionStatus: 'complete', reminderDeferredUntil: null })}>
          {saving ? 'Saving…' : 'Mark complete & save'}
        </Button>
      </Box>
    </Box>
  );
}
